import { placeParamsToURL } from 'helpers/CommonHelper';
import { DEFAULT_CURRENCY_SYMBOL } from 'helpers/DataHelper';
import { getNotificationTimeAgo } from 'helpers/DateHelper';
import { t } from 'i18next';
import NumberFormatLabelElement from 'modules/admin/components/_common/NumberFormatLabelElement';
import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { customerMainRoutes } from 'routes/customer/CustomerRoutes';
import { liveUpdatesSelector } from 'store/customer/selectors/CustomerGlobalSelectors';
import { fetchLiveUpdates } from 'store/customer/slices/CustomerGlobalSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

// live update types returned by the /live-updates API
const LIVE_UPDATE_TYPE = {
  NEW_PRODUCT: 'new_product',
  NEW_FFL: 'new_ffl',
  DISCOUNTED_PRODUCT: 'discounted_product',
  COUPON: 'coupon',
};

const LIVE_UPDATE_LIMIT = 10;
// the ticker is a "live" feed, so pull a fresh batch every 2 minutes while the tab is visible
const LIVE_UPDATE_REFRESH_INTERVAL = 2 * 60 * 1000;
// keep at least this many items on the track so the CSS marquee loop stays seamless
const MIN_TICKER_ITEMS = 8;

interface LiveUpdateFFL {
  id: string;
  business_name: string;
  slug: string;
  logo: string | null;
}

interface LiveUpdateProduct {
  id: string;
  name: string;
  slug: string;
  price: number | null;
  discounted_percentage: number;
}

interface LiveUpdateCoupon {
  id: string;
  code: string;
  title: string | null;
  description: string | null;
  discount_type: string;
  discount_value: number;
  start_date: string | null;
  end_date: string | null;
}

interface LiveUpdate {
  type: string;
  heading: string | null;
  product: LiveUpdateProduct | null;
  ffl: LiveUpdateFFL | null;
  coupon: LiveUpdateCoupon | null;
  count: number;
  happened_at: string;
}

// stand-in for the price inside the translated sentence - swapped for the shared
// NumberFormatLabelElement so the ticker formats amounts like the rest of the app
const PRICE_PLACEHOLDER = '__price__';

const renderPrice = (text: string, price: number) => {
  const [before, after] = text.split(PRICE_PLACEHOLDER);

  return (
    <>
      {before}
      <NumberFormatLabelElement
        amount={price}
        prefix={DEFAULT_CURRENCY_SYMBOL}
        showDecimal={true}
      />
      {after}
    </>
  );
};

// text that follows the bold heading, e.g. "Glock 17" + " listed 10 mins ago for $425.00"
const getUpdateText = (update: LiveUpdate): React.ReactNode => {
  const time = getNotificationTimeAgo(update.happened_at);

  switch (update.type) {
    case LIVE_UPDATE_TYPE.NEW_PRODUCT:
      // products without a price fall back to the shorter "listed x mins ago"
      return update.product?.price
        ? renderPrice(
            t('live_update_new_product', { time, price: PRICE_PLACEHOLDER }),
            update.product.price
          )
        : t('live_update_new_product_without_price', { time });
    case LIVE_UPDATE_TYPE.NEW_FFL:
      // a brand new dealer can still be without listings
      return update.count
        ? t('live_update_new_ffl', { total: update.count, time })
        : t('live_update_new_ffl_joined', { time });
    case LIVE_UPDATE_TYPE.DISCOUNTED_PRODUCT:
      return t('live_update_discounted_product', { total: update.count });
    case LIVE_UPDATE_TYPE.COUPON:
      return t('live_update_coupon', { code: update.coupon?.code });
    default:
      return '';
  }
};

// product updates open the product, everything else opens the FFL that triggered it
const getUpdateLink = (update: LiveUpdate) => {
  if (update.type === LIVE_UPDATE_TYPE.NEW_PRODUCT && update.product?.slug) {
    return placeParamsToURL(customerMainRoutes.productDetail.path, { slug: update.product.slug });
  }

  if (update.ffl?.slug) {
    return placeParamsToURL(customerMainRoutes.fflDetail.path, { slug: update.ffl.slug });
  }

  return null;
};

const LiveUpdatesSection: React.FC = () => {
  const dispatch = useAppDispatch();
  //  const updates = [
  //   { text: 'Prolific Arms Just made this Coupon', heading: 'Prolific Arms' },
  //   { text: 'Glock 17 listed 10mins ago for $425', heading: 'Glock 17' },
  //   { text: 'Prolific Arms Just discounted 10 Products', heading: 'Prolific Arms' },
  //   { text: 'Prolific Arms Listed 50 items 5mins ago', heading: 'Prolific Arms' },
  // ];
  const updates: LiveUpdate[] = useAppSelector(liveUpdatesSelector);

  useEffect(() => {
    dispatch(fetchLiveUpdates(LIVE_UPDATE_LIMIT));

    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        dispatch(fetchLiveUpdates(LIVE_UPDATE_LIMIT));
      }
    }, LIVE_UPDATE_REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Duplicate the items enough times to fill the viewport so the CSS marquee
  // looping is visually continuous and seamless.
  const tickerItems = useMemo(() => {
    if (!updates.length) return [];

    const repeatCount = Math.max(2, Math.ceil(MIN_TICKER_ITEMS / updates.length));
    return Array.from({ length: repeatCount }, () => updates).flat();
  }, [updates]);

  const renderItemText = (update: LiveUpdate) => {
    const text = getUpdateText(update);

    if (!update.heading) {
      return text;
    }

    return (
      <>
        <span className="live-updates-section__bold">{update.heading}</span> {text}
      </>
    );
  };

  if (!tickerItems.length) {
    return null;
  }

  return (
    <div className="live-updates-section">
      <div className="live-updates-section__badge">
        <span className="live-updates-section__dot" />
        <span className="live-updates-section__badge-text">{t('live')}</span>
      </div>
      <div className="live-updates-section__ticker">
        <div className="live-updates-section__track">
          {tickerItems.map((item, idx) => {
            const link = getUpdateLink(item);

            return (
              <div className="live-updates-section__item-wrapper" key={idx}>
                <span className="live-updates-section__bullet" />
                {link ? (
                  <Link className="live-updates-section__item" to={link}>
                    {renderItemText(item)}
                  </Link>
                ) : (
                  <span className="live-updates-section__item">{renderItemText(item)}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LiveUpdatesSection;
